import React, { useId } from "react";
import { TextInput } from "@app/shared/src/components_v2";
import { OrganizationDraft } from "../../helpers/organizationDraft";

/**
 * The organization form: name first, contact details under it, the address last.
 *
 * One component for the welcome page, the account dialog and the add-organization dialog, so a
 * director who has done this once recognises it everywhere. Only the name is required — a fleet
 * can be set up before anyone has looked up the office's ZIP code.
 */
export const OrganizationFields: React.FC<{
    draft: OrganizationDraft;
    onChange: (next: OrganizationDraft) => void;
    disabled?: boolean;
    /** Focus the name field on mount — right for a dialog, wrong for a page that also asks for a password. */
    autoFocus?: boolean;
}> = ({ draft, onChange, disabled, autoFocus }) => {
    // Labels are wired to their inputs by id, so a screen reader — and a test — can find a field by name.
    const ids = useId();
    const set = (key: keyof OrganizationDraft) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...draft, [key]: e.target.value });

    const field = (label: string, key: keyof OrganizationDraft, props: Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> = {}) => (
        <span className="min-w-0">
            <label htmlFor={`${ids}-${key}`} className="text-[12px] text-[var(--muted)]">
                {label}
            </label>
            <TextInput id={`${ids}-${key}`} value={draft[key]} onChange={set(key)} disabled={disabled} className="mt-1.5 w-full" {...props} />
        </span>
    );

    return (
        <>
            {field("Organization name", "name", { required: true, autoFocus, autoComplete: "organization" })}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {field("Phone", "phoneNumber", { type: "tel", autoComplete: "tel" })}
                {field("Email", "emailAddress", { type: "email", autoComplete: "email" })}
            </div>
            {field("Address", "address", { autoComplete: "address-line1" })}
            {field("Address line 2", "address2", { autoComplete: "address-line2" })}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_5rem_7rem]">
                {field("City", "city", { autoComplete: "address-level2" })}
                {field("State", "state", { autoComplete: "address-level1" })}
                {field("ZIP", "zipcode", { autoComplete: "postal-code", inputMode: "numeric" })}
            </div>
        </>
    );
};
